import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, X, Star, Trash2, Package, Wrench, Play, FileText, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LangContext";

const CATS = ["SaaS", "Hardware", "Data Service", "Consulting", "Logistics", "Marketing", "Finance", "Training", "Other"];

export function MemberProductsBody() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolve = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

  function emptyForm() {
    return { name: "", category: "SaaS", short_description: "", long_description: "", image: "", gallery: [], video_url: "", pdf_url: "", product_url: "", demo_url: "", featured: false, kind: "product" };
  }

  const load = useCallback(() => {
    if (user?.company_id) {
      api.get(`/companies/${user.company_id}/products`).then((r) => setItems(r.data)).catch(() => {});
    }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${user.company_id}/products`, form);
      toast.success(`${form.kind === "service" ? "Service" : "Produit"} ajouté`);
      setForm(emptyForm());
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleFeatured = async (p) => {
    await api.patch(`/products/${p.id}`, { featured: !p.featured });
    load();
  };
  const removeItem = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    await api.delete(`/products/${p.id}`);
    load();
  };

  if (!user?.company_id) return <div className="py-20 text-center">{t("portal_memberproducts_01")}</div>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">Produits & Services</h2>
          <p className="mt-1 text-sm text-neutral-600">{t("portal_memberproducts_02")}</p>
        </div>
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="product-new-btn">
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {open ? "Fermer" : "Nouveau"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-6 grid gap-4 rounded-2xl border border-wine-200 bg-white p-6 md:grid-cols-2">
          <div className="md:col-span-2 inline-flex rounded-full bg-wine-50/40 p-1 w-fit">
            {[{ id: "product", l: "Produit", I: Package }, { id: "service", l: "Service", I: Wrench }].map((k) => (
              <button key={k.id} type="button" onClick={() => setForm({ ...form, kind: k.id })} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold ${form.kind === k.id ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid={`product-kind-${k.id}`}>
                <k.I className="h-3.5 w-3.5" /> {k.l}
              </button>
            ))}
          </div>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="product-name" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="product-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <textarea required rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} placeholder="Description courte" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="product-short" />
          <textarea rows={4} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} placeholder={t("portal_memberproducts_90")} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="product-long" />
          <div className="md:col-span-2"><ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Image principale" testId="product-image" /></div>
          <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder={t("portal_memberproducts_91")} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="product-video" />
          <input value={form.pdf_url} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })} placeholder="URL PDF (datasheet)" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="product-pdf" />
          <input value={form.product_url} onChange={(e) => setForm({ ...form, product_url: e.target.value })} placeholder="URL produit" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="product-url" />
          <input value={form.demo_url} onChange={(e) => setForm({ ...form, demo_url: e.target.value })} placeholder={t("portal_memberproducts_92")} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="product-demo" />
          <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-wine-500" data-testid="product-featured" />
            ⭐ Mettre en avant
          </label>
          <div className="md:col-span-2"><button type="submit" className="rounded-full bg-wine-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="product-submit">{t("portal_memberproducts_93")}</button></div>
        </form>
      )}

      <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <div key={p.id} className="overflow-hidden rounded-2xl border border-neutral-100 bg-white" data-testid={`product-card-${p.id}`}>
            {p.image && <img src={resolve(p.image)} alt={p.name} className="aspect-[16/9] w-full object-cover" />}
            <div className="p-5">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${p.kind === "service" ? "bg-blue-50 text-blue-700" : "bg-wine-50 text-wine-700"}`}>{p.kind}</span>
                {p.featured && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
              </div>
              <h3 className="mt-2 text-lg font-bold text-navy">{p.name}</h3>
              <p className="mt-1 text-xs text-neutral-500">{p.category}</p>
              <p className="mt-2 line-clamp-3 text-sm text-neutral-600">{p.short_description}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-wine-700">
                {p.video_url && <span className="inline-flex items-center gap-1"><Play className="h-3 w-3" />{t("portal_memberproducts_03")}</span>}
                {p.pdf_url && <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> PDF</span>}
                {p.product_url && <span className="inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Site</span>}
                {p.demo_url && <span className="inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />{t("portal_memberproducts_04")}</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => toggleFeatured(p)} className="text-xs font-semibold text-wine-700 hover:underline" data-testid={`product-feature-${p.id}`}>{p.featured ? "Retirer ⭐" : "Mettre ⭐"}</button>
                <button onClick={() => removeItem(p)} className="ml-auto text-xs text-red-600 hover:underline" data-testid={`product-delete-${p.id}`}><Trash2 className="inline h-3 w-3" /> {t("portal_memberproducts_94")}</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="md:col-span-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500" data-testid="products-empty">{t("portal_memberproducts_05")}</div>}
      </div>
    </div>
  );
}

export default function MemberProducts() {
  return (
    <PortalLayout sidebar="member">
      <MemberProductsBody />
    </PortalLayout>
  );
}
